/**
 * Utilidades para calcular posiciones de texto dentro de un rectángulo contenedor
 * Soporta 9 combinaciones de alineación: left/center/right + top/center/bottom
 */

/**
 * Calcula la posición X del texto dentro del rectángulo según la alineación horizontal
 * @param {string} horizontalAlign - 'left', 'center', 'right'
 * @param {number} containerX - Posición X del rectángulo (0-1)
 * @param {number} containerWidth - Ancho del rectángulo (0-1)
 * @param {number} svgWidth - Ancho total del SVG en píxeles
 * @returns {number} Posición X en píxeles
 */
export function calculateTextXInContainer(horizontalAlign, containerX, containerWidth, svgWidth) {
  const containerStartX = containerX * svgWidth
  const containerPixelWidth = containerWidth * svgWidth
  
  switch (horizontalAlign) {
    case 'left':
      return containerStartX
    case 'right':
      return containerStartX + containerPixelWidth
    case 'center':
    default:
      return containerStartX + (containerPixelWidth / 2)
  }
}

/**
 * Calcula la posición Y del texto dentro del rectángulo según la alineación vertical
 * @param {string} verticalAlign - 'top', 'center', 'bottom'
 * @param {number} containerY - Posición Y del rectángulo (0-1)
 * @param {number} containerHeight - Alto del rectángulo (0-1)
 * @param {number} svgHeight - Alto total del SVG en píxeles
 * @param {number} textHeight - Alto del texto en píxeles (opcional, para ajuste fino)
 * @returns {number} Posición Y en píxeles
 */
export function calculateTextYInContainer(verticalAlign, containerY, containerHeight, svgHeight, textHeight = 0) {
  const containerStartY = containerY * svgHeight
  const containerPixelHeight = containerHeight * svgHeight
  
  switch (verticalAlign) {
    case 'top':
      return containerStartY + textHeight / 2 // Ajustar para que el texto no se salga por arriba
    case 'bottom':
      return containerStartY + containerPixelHeight - textHeight / 2 // Ajustar para que el texto no se salga por abajo
    case 'center':
    default:
      return containerStartY + (containerPixelHeight / 2)
  }
}

/**
 * Parsea la alineación del contenedor en componentes horizontal y vertical
 * @param {string} align - Formato: 'left-top', 'center-center', 'right-bottom', etc.
 * @returns {Object} { horizontal: 'left'|'center'|'right', vertical: 'top'|'center'|'bottom' }
 */
export function parseContainerAlign(align) {
  const parts = (align || 'center-center').split('-')
  const horizontal = parts[0] || 'center'
  const vertical = parts[1] || 'center'
  
  return {
    horizontal: ['left', 'center', 'right'].includes(horizontal) ? horizontal : 'center',
    vertical: ['top', 'center', 'bottom'].includes(vertical) ? vertical : 'center'
  }
}

/**
 * Obtiene el text-anchor SVG según la alineación horizontal
 * @param {string} horizontalAlign - 'left', 'center', 'right'
 * @returns {string} 'start', 'middle', 'end'
 */
export function getTextAnchor(horizontalAlign) {
  switch (horizontalAlign) {
    case 'left':
      return 'start'
    case 'right':
      return 'end'
    case 'center':
    default:
      return 'middle'
  }
}

/**
 * Obtiene el dominant-baseline SVG según la alineación vertical
 * @param {string} verticalAlign - 'top', 'center', 'bottom'
 * @returns {string} 'top', 'middle', 'bottom'
 */
export function getDominantBaseline(verticalAlign) {
  switch (verticalAlign) {
    case 'top':
      return 'top'
    case 'bottom':
      return 'bottom'
    case 'center':
    default:
      return 'middle'
  }
}

/**
 * Calcula el tamaño de fuente ajustado para que el texto quepa en el ancho disponible
 * @param {string} text - Texto a medir
 * @param {number} initialFontSize - Tamaño de fuente inicial
 * @param {number} maxWidth - Ancho máximo disponible en píxeles
 * @param {string} fontFamily - Familia de fuente
 * @param {string} fontWeight - Peso de fuente
 * @param {SVGElement} svg - Elemento SVG para medir
 * @returns {number} Tamaño de fuente ajustado
 */
export function calculateAutoFitFontSize(text, initialFontSize, maxWidth, fontFamily, fontWeight, svg) {
  if (!text || !svg) return initialFontSize
  
  // Crear elemento temporal para medir
  const tempText = document.createElementNS('http://www.w3.org/2000/svg', 'text')
  tempText.setAttribute('font-family', fontFamily || 'Arial, sans-serif')
  tempText.setAttribute('font-weight', fontWeight || 'normal')
  tempText.setAttribute('font-size', initialFontSize)
  tempText.textContent = text
  tempText.setAttribute('visibility', 'hidden')
  
  svg.appendChild(tempText)
  const textWidth = tempText.getComputedTextLength()
  svg.removeChild(tempText)
  
  // Si el texto ya cabe, usar el tamaño inicial
  if (textWidth <= maxWidth) {
    return initialFontSize
  }
  
  // Calcular factor de escala
  const scaleFactor = maxWidth / textWidth
  return initialFontSize * scaleFactor
}

/**
 * Divide un texto en múltiples líneas para que quepa en el ancho disponible
 * @param {string} text - Texto a dividir
 * @param {number} fontSize - Tamaño de fuente
 * @param {number} maxWidth - Ancho máximo disponible en píxeles
 * @param {string} fontFamily - Familia de fuente
 * @param {string} fontWeight - Peso de fuente
 * @param {SVGElement} svg - Elemento SVG para medir
 * @returns {Array<string>} Array de líneas de texto
 */
export function wrapText(text, fontSize, maxWidth, fontFamily, fontWeight, svg) {
  if (!text || !svg) return [text]
  
  const words = text.split(' ')
  const lines = []
  let currentLine = ''
  
  // Crear elemento temporal para medir
  const tempText = document.createElementNS('http://www.w3.org/2000/svg', 'text')
  tempText.setAttribute('font-family', fontFamily || 'Arial, sans-serif')
  tempText.setAttribute('font-weight', fontWeight || 'normal')
  tempText.setAttribute('font-size', fontSize)
  tempText.setAttribute('visibility', 'hidden')
  svg.appendChild(tempText)
  
  for (let i = 0; i < words.length; i++) {
    const testLine = currentLine ? `${currentLine} ${words[i]}` : words[i]
    tempText.textContent = testLine
    const testWidth = tempText.getComputedTextLength()
    
    if (testWidth > maxWidth) {
      // La línea actual es demasiado larga
      if (currentLine) {
        // Guardar la línea anterior y empezar nueva con la palabra actual
        lines.push(currentLine)
        currentLine = words[i]
      } else {
        // Si una sola palabra es más larga que el ancho, forzar el salto
        // Dividir la palabra si es muy larga (aunque no es ideal, es mejor que desbordarse)
        if (words[i].length > 0) {
          lines.push(words[i])
          currentLine = ''
        }
      }
    } else {
      // La línea cabe, agregar la palabra
      currentLine = testLine
    }
  }
  
  // Agregar la última línea si existe
  if (currentLine) {
    lines.push(currentLine)
  }
  
  // Asegurar que siempre haya al menos una línea
  if (lines.length === 0) {
    lines.push(text)
  }
  
  svg.removeChild(tempText)
  return lines.length > 0 ? lines : [text]
}

/**
 * Calcula las posiciones del título y subtítulo dentro del rectángulo contenedor
 * @param {Object} config - Configuración del contenedor y texto
 * @param {number} config.containerX - Posición X del rectángulo (0-1)
 * @param {number} config.containerY - Posición Y del rectángulo (0-1)
 * @param {number} config.containerWidth - Ancho del rectángulo (0-1)
 * @param {number} config.containerHeight - Alto del rectángulo (0-1)
 * @param {string} config.containerAlign - Alineación: 'left-top', 'center-center', etc.
 * @param {number} config.svgWidth - Ancho total del SVG en píxeles
 * @param {number} config.svgHeight - Alto total del SVG en píxeles
 * @param {number} config.titleFontSize - Tamaño de fuente del título en píxeles
 * @param {number} config.subtitleFontSize - Tamaño de fuente del subtítulo en píxeles
 * @param {string} config.titleText - Texto del título
 * @param {string} config.subtitleText - Texto del subtítulo
 * @param {boolean} config.titleAutoFit - Autoajustar título al ancho (se calcula automáticamente como !titleWrap)
 * @param {boolean} config.titleWrap - Permitir salto de línea en título (default: false). Si está activado, hace wrap; si no, autoFit automático
 * @param {boolean} config.subtitleAutoFit - Autoajustar subtítulo al ancho (se calcula automáticamente como !subtitleWrap)
 * @param {boolean} config.subtitleWrap - Permitir salto de línea en subtítulo (default: false). Si está activado, hace wrap; si no, autoFit automático
 * @param {string} config.fontFamily - Familia de fuente
 * @param {string} config.titleFontWeight - Peso de fuente del título
 * @param {string} config.subtitleFontWeight - Peso de fuente del subtítulo
 * @param {SVGElement} config.svg - Elemento SVG para medir texto
 * @returns {Object} Posiciones calculadas para título y subtítulo con ajustes aplicados
 */
export function calculateTextPositionsInContainer(config) {
  const {
    containerX = 0.1,
    containerY = 0.1,
    containerWidth = 0.8,
    containerHeight = 0.3,
    containerAlign = 'center-center',
    svgWidth = 100,
    svgHeight = 100,
    titleFontSize = 8,
    subtitleFontSize = 6,
    titleText = '',
    subtitleText = '',
    titleAutoFit = true,
    titleWrap = false,
    subtitleAutoFit = true,
    subtitleWrap = false,
    fontFamily = 'Arial, sans-serif',
    titleFontWeight = 'bold',
    subtitleFontWeight = 'normal',
    svg = null
  } = config
  
  // Parsear alineación
  const { horizontal, vertical } = parseContainerAlign(containerAlign)
  
  // Calcular dimensiones del contenedor en píxeles
  const containerPixelX = containerX * svgWidth
  const containerPixelY = containerY * svgHeight
  const containerPixelWidth = containerWidth * svgWidth
  const containerPixelHeight = containerHeight * svgHeight
  
  // Calcular posición X (igual para título y subtítulo)
  const textX = calculateTextXInContainer(horizontal, containerX, containerWidth, svgWidth)
  const textAnchor = getTextAnchor(horizontal)
  
  // Calcular ancho disponible para el texto (con un pequeño margen)
  const availableTextWidth = containerPixelWidth * 0.95 // 95% del ancho del contenedor
  
  // Crear elemento temporal para medir texto (se usará para truncar si es necesario)
  const tempText = svg ? document.createElementNS('http://www.w3.org/2000/svg', 'text') : null
  if (tempText && svg) {
    tempText.setAttribute('visibility', 'hidden')
    tempText.setAttribute('font-family', fontFamily || 'Arial, sans-serif')
    svg.appendChild(tempText)
  }
  
  // Aplicar autoajuste o salto de línea al título
  // Reglas: wrap y autoFit son mutuamente excluyentes (toggle)
  // - Si wrap está activado: hacer salto de línea (máximo 2 líneas)
  // - Si wrap está desactivado (autoFit activado): ajustar tamaño de fuente
  let finalTitleFontSize = titleFontSize
  let titleLines = titleText && titleText.trim() ? [titleText] : []
  
  if (svg && titleText && titleText.trim()) {
    if (titleWrap) {
      // Wrap activado: dividir en múltiples líneas automáticamente cuando se llene el ancho
      // Máximo 2 líneas según la especificación
      titleLines = wrapText(
        titleText,
        titleFontSize,
        availableTextWidth,
        fontFamily,
        titleFontWeight,
        svg
      )
      // Limitar a máximo 2 líneas
      if (titleLines.length > 2 && tempText) {
        titleLines = titleLines.slice(0, 2)
        // Si hay más de 2 líneas, truncar el texto de la segunda línea si es necesario
        const secondLine = titleLines[1]
        if (secondLine) {
          tempText.setAttribute('font-size', titleFontSize)
          tempText.setAttribute('font-weight', titleFontWeight || 'bold')
          tempText.textContent = secondLine
          const secondLineWidth = tempText.getComputedTextLength()
          if (secondLineWidth > availableTextWidth) {
            // Truncar la segunda línea para que quepa
            let truncated = secondLine
            while (truncated.length > 0) {
              tempText.textContent = truncated + '...'
              if (tempText.getComputedTextLength() <= availableTextWidth) {
                titleLines[1] = truncated + '...'
                break
              }
              truncated = truncated.slice(0, -1)
            }
          }
        }
      }
    } else {
      // Wrap desactivado (autoFit activado): ajustar tamaño de fuente para que quepa en el ancho disponible
      finalTitleFontSize = calculateAutoFitFontSize(
        titleText,
        titleFontSize,
        availableTextWidth,
        fontFamily,
        titleFontWeight,
        svg
      )
    }
  }
  
  // Aplicar autoajuste o salto de línea al subtítulo
  // Reglas: wrap y autoFit son mutuamente excluyentes (toggle)
  // - Si wrap está activado: hacer salto de línea (máximo 2 líneas)
  // - Si wrap está desactivado (autoFit activado): ajustar tamaño de fuente
  let finalSubtitleFontSize = subtitleFontSize
  let subtitleLines = subtitleText && subtitleText.trim() ? [subtitleText] : []
  
  if (svg && subtitleText && subtitleText.trim()) {
    if (subtitleWrap) {
      // Wrap activado: dividir en múltiples líneas automáticamente cuando se llene el ancho
      // Máximo 2 líneas según la especificación
      subtitleLines = wrapText(
        subtitleText,
        subtitleFontSize,
        availableTextWidth,
        fontFamily,
        subtitleFontWeight,
        svg
      )
      // Limitar a máximo 2 líneas
      if (subtitleLines.length > 2 && tempText) {
        subtitleLines = subtitleLines.slice(0, 2)
        // Si hay más de 2 líneas, truncar el texto de la segunda línea si es necesario
        const secondLine = subtitleLines[1]
        if (secondLine) {
          tempText.setAttribute('font-size', subtitleFontSize)
          tempText.setAttribute('font-weight', subtitleFontWeight || 'normal')
          tempText.textContent = secondLine
          const secondLineWidth = tempText.getComputedTextLength()
          if (secondLineWidth > availableTextWidth) {
            // Truncar la segunda línea para que quepa
            let truncated = secondLine
            while (truncated.length > 0) {
              tempText.textContent = truncated + '...'
              if (tempText.getComputedTextLength() <= availableTextWidth) {
                subtitleLines[1] = truncated + '...'
                break
              }
              truncated = truncated.slice(0, -1)
            }
          }
        }
      }
    } else {
      // Wrap desactivado (autoFit activado): ajustar tamaño de fuente para que quepa en el ancho disponible
      finalSubtitleFontSize = calculateAutoFitFontSize(
        subtitleText,
        subtitleFontSize,
        availableTextWidth,
        fontFamily,
        subtitleFontWeight,
        svg
      )
    }
  }
  
  // Limpiar elemento temporal
  if (tempText && svg && svg.contains(tempText)) {
    svg.removeChild(tempText)
  }
  
  // Calcular altura total del texto (considerando múltiples líneas)
  const titleLineHeight = finalTitleFontSize * 1.2 // Line height ajustado para títulos con múltiples líneas
  const subtitleLineHeight = finalSubtitleFontSize * 1.2
  // La altura real del bloque de líneas es: (líneas - 1) * lineHeight + fontSize
  // Porque las líneas se distribuyen alrededor del centro con dominantBaseline: 'middle'
  // Si no hay líneas, la altura es 0
  const titleBlockHeight = titleLines.length === 0 
    ? 0
    : titleLines.length > 1 
      ? ((titleLines.length - 1) * titleLineHeight) + finalTitleFontSize
      : finalTitleFontSize
  const subtitleBlockHeight = subtitleLines.length === 0
    ? 0
    : subtitleLines.length > 1
      ? ((subtitleLines.length - 1) * subtitleLineHeight) + finalSubtitleFontSize
      : finalSubtitleFontSize
  // Mantener titleTotalHeight y subtitleTotalHeight para compatibilidad con cálculos existentes
  const titleTotalHeight = titleBlockHeight
  const subtitleTotalHeight = subtitleBlockHeight
  
  // Calcular posición Y según la alineación vertical
  let titleY, subtitleY
  // El subtítulo debe estar justo después del límite bottom del título
  // Usar un espaciado mínimo basado en el tamaño de fuente del título
  const spacingPixels = finalTitleFontSize * 0.05 // 5% del tamaño de fuente como espaciado mínimo
  
  // Calcular dónde termina realmente la última línea del título
  // titleY es el centro del bloque completo cuando hay múltiples líneas
  // En el renderizado, las líneas se distribuyen alrededor de titleY:
  // - Primera línea (index 0): titleY - ((lines.length - 1) * lineHeight / 2)
  // - Última línea: titleY + ((lines.length - 1) * lineHeight / 2)
  // Como dominantBaseline: 'middle', el final de la última línea está en:
  // lastLineY + finalTitleFontSize / 2
  const titleEndY = (baseTitleY) => {
    if (titleLines.length === 0) {
      // Si no hay líneas, no hay final
      return baseTitleY
    } else if (titleLines.length === 1) {
      // Si solo hay una línea, el final está en titleY + fontSize / 2
      return baseTitleY + (finalTitleFontSize / 2)
    } else {
      // Si hay múltiples líneas, titleY es el centro del bloque
      // La última línea está en: titleY + ((titleLines.length - 1) * titleLineHeight / 2)
      // Y su final está en: esa posición + fontSize / 2
      const lastLineY = baseTitleY + ((titleLines.length - 1) * titleLineHeight / 2)
      return lastLineY + (finalTitleFontSize / 2)
    }
  }
  
  // Calcular altura total del bloque de texto (título + espaciado + subtítulo)
  const totalTextHeight = titleTotalHeight + spacingPixels + subtitleTotalHeight
  
  switch (vertical) {
    case 'top':
      // Título al inicio, subtítulo debajo
      // Para múltiples líneas, el bloque comienza en containerPixelY
      // titleY es el centro del bloque, que está en: containerPixelY + titleBlockHeight/2
      if (titleLines.length === 0) {
        titleY = containerPixelY
      } else if (titleLines.length === 1) {
        titleY = containerPixelY + (finalTitleFontSize / 2)
      } else {
        titleY = containerPixelY + (titleBlockHeight / 2)
      }
      // El subtítulo debe comenzar justo después de donde termina la última línea del título
      // Si no hay título, el subtítulo comienza en containerPixelY
      if (titleLines.length === 0) {
        subtitleY = containerPixelY + (finalSubtitleFontSize / 2)
      } else {
        // subtitleY es el centro de la primera línea del subtítulo (dominantBaseline: 'middle')
        // El inicio de la primera línea del subtítulo está en: subtitleY - finalSubtitleFontSize / 2
        // Queremos que ese inicio esté en: titleEndY(titleY) + spacingPixels
        // Entonces: subtitleY - finalSubtitleFontSize / 2 = titleEndY(titleY) + spacingPixels
        // subtitleY = titleEndY(titleY) + spacingPixels + finalSubtitleFontSize / 2
        subtitleY = titleEndY(titleY) + spacingPixels + (finalSubtitleFontSize / 2)
      }
      break
      
    case 'bottom':
      // Subtítulo al final, título arriba
      subtitleY = containerPixelY + containerPixelHeight - (finalSubtitleFontSize / 2)
      // Calcular titleY hacia atrás desde donde comienza el subtítulo
      if (titleLines.length === 0) {
        // Si no hay título, no calcular posición
        titleY = containerPixelY
      } else {
        // subtitleY es el centro del subtítulo, así que el inicio del subtítulo está en: subtitleY - (finalSubtitleFontSize / 2)
        // El final del título debe estar en: (subtitleY - finalSubtitleFontSize / 2) - spacingPixels
        const titleEndBeforeSubtitle = (subtitleY - finalSubtitleFontSize / 2) - spacingPixels
        // Calcular titleY hacia atrás desde titleEndBeforeSubtitle
        // titleEndY(titleY) = titleEndBeforeSubtitle
        if (titleLines.length === 1) {
          // titleY + finalTitleFontSize / 2 = titleEndBeforeSubtitle
          titleY = titleEndBeforeSubtitle - (finalTitleFontSize / 2)
        } else {
          // titleY es el centro del bloque, el final está en: titleY + titleBlockHeight/2
          // titleY + titleBlockHeight/2 = titleEndBeforeSubtitle
          titleY = titleEndBeforeSubtitle - (titleBlockHeight / 2)
        }
      }
      break
      
    case 'center':
    default:
      // Centrar el bloque de texto (título + subtítulo) en el contenedor
      const centerY = containerPixelY + (containerPixelHeight / 2)
      const textBlockStartY = centerY - (totalTextHeight / 2)
      // Para centrar correctamente el bloque de líneas del título:
      // - Si no hay líneas: titleY = centerY (no se renderizará)
      // - Si hay 1 línea: titleY es el centro de esa línea = textBlockStartY + fontSize/2
      // - Si hay múltiples líneas: titleY es el centro del bloque completo
      //   El bloque comienza en textBlockStartY y tiene altura titleBlockHeight
      //   El centro está en textBlockStartY + titleBlockHeight/2
      if (titleLines.length === 0) {
        titleY = centerY
      } else if (titleLines.length === 1) {
        titleY = textBlockStartY + (finalTitleFontSize / 2)
      } else {
        // titleY es el centro del bloque completo de líneas
        titleY = textBlockStartY + (titleBlockHeight / 2)
      }
      // El subtítulo debe comenzar justo después de donde termina la última línea del título
      // Si no hay título, centrar el subtítulo
      if (titleLines.length === 0) {
        subtitleY = centerY
      } else {
        subtitleY = titleEndY(titleY) + spacingPixels + (finalSubtitleFontSize / 2)
      }
      break
  }
  
  const dominantBaseline = getDominantBaseline(vertical)
  
  return {
    title: {
      x: textX,
      y: titleY,
      textAnchor: textAnchor,
      dominantBaseline: 'middle',
      fontSize: finalTitleFontSize,
      lines: titleLines,
      lineHeight: titleLineHeight
    },
    subtitle: {
      x: textX,
      y: subtitleY,
      textAnchor: textAnchor,
      dominantBaseline: 'middle',
      fontSize: finalSubtitleFontSize,
      lines: subtitleLines,
      lineHeight: subtitleLineHeight
    }
  }
}

